import { useCallback, useMemo, useState } from 'react'
import { orderBy } from 'es-toolkit'
import toast from 'react-hot-toast'
import { useCreatePartnership, useInvitePartnership } from '@/shared/hooks/api/partnerships'
import useI18n from '@/shared/hooks/useI18n'
import { useSession } from '@/shared/hooks/useSession'
import {
  Button,
  GenericDialog,
  InputGroup,
  EU_COUNTRIES,
  partnershipInvitationPayloadSchema,
  GenericCombobox
} from '@/common'
import EmailInputGroup from '@/shared/components/forms/EmailInputGroup'

export default function CreateImporterDialog({
  isOpen,
  close
}: {
  isOpen: boolean
  close: () => void
}) {
  const {
    session: { organization }
  } = useSession()
  const { t } = useI18n()
  const { mutateAsync: createPartnership, isPending: isPartnershipPending } = useCreatePartnership(
    organization.id
  )
  const [supplierName, setSupplierName] = useState('')
  const [supplierEmail, setSupplierEmail] = useState('')
  const { mutateAsync: sendInvitation, isPending: isInvitationPending } = useInvitePartnership(
    organization.id
  )

  const [selectedCountry, setSelectedCountry] = useState('')
  const memberStatesOptions = orderBy(
    EU_COUNTRIES.map(country => ({
      label: t(`countriesByIsoAlpha2:${country}`),
      value: country
    })),
    [option => option.label.toLowerCase().normalize('NFKD')],
    ['asc']
  )

  const onClose = useCallback(() => {
    setSupplierName('')
    setSelectedCountry('')
    setSupplierEmail('')
    close()
  }, [close])

  const onSave = useCallback(async () => {
    try {
      const partnership = await createPartnership({
        name: supplierName,
        country: selectedCountry,
        operatorOrganizationId: organization.id
      })

      const validation = partnershipInvitationPayloadSchema.safeParse({
        email: supplierEmail
      })

      if (!validation.success) {
        toast.error(
          t('importers:importersList.inviteImporterDialog.notifications.invalidEmailAddress')
        )
        return
      }

      await sendInvitation({
        partnershipId: partnership.id,
        payload: validation.data
      })

      toast.success(t('importers:importersList.inviteImporterDialog.notifications.invitationSent'))
      onClose()
    } catch {
      toast.error(
        t('importers:importersList.inviteImporterDialog.notifications.failedToSendInvitation')
      )
    }
  }, [
    createPartnership,
    supplierName,
    selectedCountry,
    organization.id,
    supplierEmail,
    sendInvitation,
    t,
    onClose
  ])

  const isSaveEnabled = useMemo(() => {
    return selectedCountry && supplierName && supplierEmail
  }, [selectedCountry, supplierName, supplierEmail])

  return (
    <GenericDialog
      size="md"
      isOpen={isOpen}
      close={onClose}
      title={t('importers:createImporterDialog.title')}
    >
      <div className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-4">
            <GenericCombobox
              items={memberStatesOptions}
              value={memberStatesOptions.find(option => option.value === selectedCountry) || null}
              onChange={evt => setSelectedCountry(evt?.value || '')}
              label={t('importers:createImporterDialog.fields.country.label')}
            />
            <InputGroup
              label={t('importers:createImporterDialog.fields.name.label')}
              value={supplierName}
              onChange={e => setSupplierName(e.target.value)}
            />
            <EmailInputGroup
              label={t('importers:importersList.inviteImporterDialog.fields.email.label')}
              value={supplierEmail}
              onChange={e => setSupplierEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="gap-2 flex justify-end">
          <Button
            variant="neutral"
            onClick={onClose}
            disabled={isInvitationPending || isPartnershipPending}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={onSave}
            disabled={!isSaveEnabled || isPartnershipPending || isInvitationPending}
          >
            {t('common:actions.invite')}
          </Button>
        </div>
      </div>
    </GenericDialog>
  )
}
